import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  Package,
  ChevronDown,
  ChevronRight,
  Clock,
  Route,
  RouteOff,
} from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  ConnectionIndicator,
  CopyButton,
  JsonContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useToast,
} from '@/components/ui';
import { usePlatformEvents } from '../../contexts/PlatformEventContext';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import {
  useIntegrationLiveEvents,
  type LiveIntegrationEvent,
} from '../../hooks/useIntegrationLiveEvents';
import type {
  DynamicResolution,
  IntegrationEvent,
  IntegrationEventDetail,
  IntegrationRedeliverResult,
} from '../../types/integrations';

/**
 * The card is data-source agnostic: callers inject the REST hooks for their
 * surface (space-scoped `useIntegrations` or org-scoped `useOrgIntegrations`),
 * so the same UI serves both. The live-WS hook stays internal — it is already
 * scoped purely by integration id, not by space.
 */
export type UseEventsHook = (
  id: string | undefined,
) => { data?: IntegrationEvent[]; isLoading: boolean };

export type UseEventDetailHook = (
  id: string | undefined,
  eventId: string | null,
) => { data?: IntegrationEventDetail; isLoading: boolean };

export type UseRedeliverHook = (
  id: string | undefined,
) => {
  mutateAsync: (eventId: string) => Promise<IntegrationRedeliverResult>;
  isPending: boolean;
};

interface Props {
  integrationId: string;
  useEvents: UseEventsHook;
  useEventDetail: UseEventDetailHook;
  /**
   * Omit to render the card read-only. Surfaces where the viewer doesn't own
   * the integration (e.g. a space looking at an org-owned subscription) pass
   * nothing and the Redeliver button is hidden.
   */
  useRedeliver?: UseRedeliverHook;
  /** react-query key prefix for the historical list, so live events can
   *  invalidate the right cache. e.g. ['org','integrations',id]. */
  queryKeyPrefix?: readonly unknown[];
  /**
   * When provided, each matched routing rule renders as a link to this href
   * (e.g. the rules tab with `?selRule=<id>`). Omitted on the space-scoped
   * surface, which has no rules tab to deep-link into.
   */
  ruleHref?: (ruleId: string) => string;
  title?: string;
  description?: string;
  /**
   * Suppress the per-card connection indicator. Set by surfaces that render
   * a page-level indicator on their tab bar (e.g. the org integration detail
   * page) so the dot isn't duplicated when this card is mounted.
   */
  hideConnectionIndicator?: boolean;
}

function isJsonContentType(contentType: string | undefined): boolean {
  return !!contentType && /json/i.test(contentType);
}

function serializeHeaders(headers: Record<string, string>): string | undefined {
  const entries = Object.entries(headers);
  if (entries.length === 0) return undefined;
  return entries.map(([k, v]) => `${k}: ${v}`).join('\n');
}

/** Unified row that renders both historical (REST) and live (WS) events. */
export interface DeliveryRow {
  key: string; // stable key for React + expansion tracking
  eventId: string;
  displayId: string; // deliveryId when available, else eventId
  /** Provider-native delivery id (e.g. Gmail message id), when distinct from
   *  the canonical event id. Surfaced in the expanded detail. */
  deliveryId?: string;
  eventType: string;
  status: 'success' | 'failed' | string;
  errorMessage?: string;
  createdAt: string;
  processingDurationMs?: number;
  /** undefined = unknown (live-only row before REST enrichment). */
  routed?: boolean;
  targetSpaceIds: string[];
  matchedRuleIds: string[];
  /** Present when a dynamic rule resolved the target space at event time. */
  dynamicResolution?: DynamicResolution;
  /**
   * Merged multi-integration lists set these so one row can fetch its own
   * detail and carry a tag. Single-integration usage leaves them undefined and
   * relies on the `integrationId` prop instead.
   */
  integrationId?: string;
  /** Short tag shown on the row identifying its source (e.g. "Gmail"). */
  sourceLabel?: string;
}

function stripComPrefix(eventType: string): string {
  if (eventType.startsWith('com.')) {
    const parts = eventType.split('.');
    return parts.slice(2).join('.') || eventType;
  }
  return eventType;
}

function historicalToRow(event: IntegrationEvent): DeliveryRow {
  return {
    key: event.eventId,
    eventId: event.eventId,
    displayId: event.deliveryId ?? event.eventId,
    ...(event.deliveryId ? { deliveryId: event.deliveryId } : {}),
    eventType: event.eventType,
    status: event.status,
    errorMessage: event.errorMessage,
    createdAt: event.createdAt,
    processingDurationMs: event.processingDurationMs,
    routed: event.routed,
    targetSpaceIds: event.matchedTargetSpaceIds ?? [],
    matchedRuleIds: event.matchedRuleIds ?? [],
    dynamicResolution: event.dynamicResolution,
  };
}

function liveToRow(event: LiveIntegrationEvent): DeliveryRow {
  // Prefer the integration event id (iev-, from the platform envelope's
  // subjectId) for `eventId` so the detail endpoint — keyed on the integration
  // event id — works for rows that are still live-only, and so the row
  // collapses against the matching historical REST row in mergeRows. Fall
  // back to the platform evt- id only as a defensive safety net.
  const integrationEventId = event.integrationEventId ?? event.eventId;
  return {
    key: integrationEventId,
    eventId: integrationEventId,
    displayId: integrationEventId,
    eventType: event.eventType,
    status: event.status,
    errorMessage: event.errorMessage,
    createdAt: event.createdAt,
    // Live envelope doesn't carry routing; REST enrichment fills these in.
    routed: undefined,
    targetSpaceIds: [],
    matchedRuleIds: [],
  };
}

/**
 * Merge historical REST rows with live WS rows. Dedupe by the most stable
 * identifier available: deliveryId if set, else eventId.
 */
export function mergeRows(
  historical: IntegrationEvent[] | undefined,
  live: LiveIntegrationEvent[],
): DeliveryRow[] {
  const seen = new Map<string, DeliveryRow>();

  // Dedupe on the integration event id (iev-…), which BOTH row types carry in
  // `eventId`: historical rows from the REST list, live rows from the platform
  // envelope's subjectId/integrationEvent.id. Keying on `displayId` was buggy —
  // it resolves to the *provider* deliveryId for providers that set one (Gmail
  // messageId, Calendar `${eventId}:${updated}`, GitHub x-github-delivery),
  // which differs from the live row's iev-keyed displayId, so the same event
  // rendered twice. (Chat happened to work only because it has no deliveryId.)
  const keyFor = (row: DeliveryRow) => row.eventId;

  for (const e of live) {
    const row = liveToRow(e);
    seen.set(keyFor(row), row);
  }

  for (const e of historical ?? []) {
    const row = historicalToRow(e);
    const k = keyFor(row);
    const existing = seen.get(k);
    if (!existing) {
      seen.set(k, row);
    } else {
      // Enrich the live row with metadata fields it doesn't carry.
      // The provider delivery id only exists on the historical row, so backfill
      // it (and the displayId) so the merged row still shows it.
      if (!existing.deliveryId && e.deliveryId) {
        existing.deliveryId = e.deliveryId;
        existing.displayId = e.deliveryId;
      }
      if (existing.processingDurationMs == null && e.processingDurationMs != null) {
        existing.processingDurationMs = e.processingDurationMs;
      }
      if (!existing.errorMessage && e.errorMessage) {
        existing.errorMessage = e.errorMessage;
      }
      if (existing.routed == null && e.routed != null) {
        existing.routed = e.routed;
      }
      if (existing.targetSpaceIds.length === 0 && e.matchedTargetSpaceIds?.length) {
        existing.targetSpaceIds = e.matchedTargetSpaceIds;
      }
      if (existing.matchedRuleIds.length === 0 && e.matchedRuleIds?.length) {
        existing.matchedRuleIds = e.matchedRuleIds;
      }
      if (!existing.dynamicResolution && e.dynamicResolution) {
        existing.dynamicResolution = e.dynamicResolution;
      }
    }
  }

  return Array.from(seen.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

function StatusIcon({ status }: { status: string }) {
  const failed = status === 'failed' || status === 'error';
  return failed ? (
    <X className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0" aria-label="failed" />
  ) : (
    <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" aria-label="success" />
  );
}

/**
 * Where the event landed, as one rounded pill per target space: a route icon
 * + the space name (resolved when the viewer can see the space, else the id).
 * When a matching rule id and `ruleHref` are available the pill is a link into
 * the rules tab; otherwise it's static text. A muted `RouteOff` pill marks
 * events that matched no rule. Renders nothing while routing is still unknown
 * (live-only row awaiting REST enrichment).
 */
function DeliveryTargets({
  routed,
  targetSpaceIds,
  matchedRuleIds,
  dynamicResolution,
  resolveSpaceName,
  ruleHref,
}: {
  routed?: boolean;
  targetSpaceIds: string[];
  matchedRuleIds: string[];
  dynamicResolution?: DynamicResolution;
  resolveSpaceName: (id: string) => string | undefined;
  ruleHref?: (ruleId: string) => string;
}) {
  if (targetSpaceIds.length > 0) {
    const dynamic = !!dynamicResolution;
    return (
      <span className="flex items-center gap-1.5 min-w-0">
        {targetSpaceIds.map((id, i) => {
          const name = resolveSpaceName(id);
          // A dynamically-resolved target is often another user's personal
          // space, which the viewer can't name — fall back to a neutral label
          // rather than showing a raw spc- id.
          const label = name ?? (dynamic ? 'a personal space' : id);
          // Target spaces and matched rules are index-aligned (one rule →
          // its target space). Pair them so the pill links to the rule that
          // actually routed the event here.
          const ruleId = matchedRuleIds[i];
          const title = dynamic
            ? `Routed to ${name ?? 'a personal space'} via DM-sender lookup`
            : name
              ? `Routed to ${name} (${id})`
              : `Routed to ${id}`;
          const pill =
            'inline-flex items-center gap-1 rounded-full border border-blue-500/25 bg-blue-500/10 pl-1.5 pr-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 max-w-[14rem]';
          const icon = (
            <Route className="h-3 w-3 flex-shrink-0 opacity-70" />
          );
          if (ruleId && ruleHref) {
            return (
              <Link
                key={id}
                to={ruleHref(ruleId)}
                // Don't toggle the row's expand/collapse.
                onClick={(e) => e.stopPropagation()}
                title={
                  dynamic ? title : `Routed to ${label} — view the matching rule`
                }
                className={`${pill} transition-colors hover:border-blue-500/50 hover:bg-blue-500/20 focus-ring`}
              >
                {icon}
                <span className="truncate">{label}</span>
              </Link>
            );
          }
          return (
            <span key={id} title={title} className={pill}>
              {icon}
              <span className="truncate">{label}</span>
            </span>
          );
        })}
        {dynamic && (
          <span
            title="Target resolved from the DM sender at delivery time"
            className="text-xs text-muted-foreground whitespace-nowrap"
          >
            via DM sender
          </span>
        )}
      </span>
    );
  }
  if (routed === false) {
    return (
      <span
        title="Received but matched no routing rule — not forwarded to any space"
        className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
      >
        <RouteOff className="h-3 w-3 flex-shrink-0 opacity-70" />
        Not routed
      </span>
    );
  }
  return null;
}

export function DeliveryRowView({
  row,
  integrationId,
  expanded,
  onToggle,
  useEventDetail,
  useRedeliver,
  resolveSpaceName,
  ruleHref,
  hideTargets = false,
  idMode = 'display',
}: {
  row: DeliveryRow;
  integrationId: string;
  expanded: boolean;
  onToggle: () => void;
  useEventDetail: UseEventDetailHook;
  useRedeliver?: UseRedeliverHook;
  resolveSpaceName: (id: string) => string | undefined;
  ruleHref?: (ruleId: string) => string;
  /** Hide the routed-to-space pills. Set on space-scoped surfaces where the
   *  target is always the current space, so the pill is redundant. */
  hideTargets?: boolean;
  /**
   * Which id to show in the row. `'display'` (default) shows the provider
   * delivery id when available (legacy behavior). `'event'` always shows the
   * canonical `iev-…` id for a consistent list; the provider delivery id then
   * appears in the expanded detail. Used by the Google card so Gmail (which has
   * a message id) and Chat (which doesn't) read consistently.
   */
  idMode?: 'display' | 'event';
}) {
  // Merged lists carry the source integration on the row itself; single-source
  // lists fall back to the card-level prop.
  const rowIntegrationId = row.integrationId ?? integrationId;
  const shownId = idMode === 'event' ? row.eventId : row.displayId;
  const shortType = stripComPrefix(row.eventType);
  const failed = row.status === 'failed' || row.status === 'error';
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <div className="border-b last:border-b-0">
      {/* Row is a div (not a button) because it embeds a CopyButton —
          nesting interactive elements is invalid HTML. */}
      <div
        role="button"
        tabIndex={0}
        data-testid="delivery-row"
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-accent/50 transition-colors text-left cursor-pointer focus-ring"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        )}
        <StatusIcon status={row.status} />
        <Package className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <span className="font-mono text-xs text-muted-foreground" title={shownId}>
          {shownId}
        </span>
        <CopyButton value={shownId} />
        {row.sourceLabel && (
          <span className="inline-flex w-16 flex-shrink-0 items-center justify-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {row.sourceLabel}
          </span>
        )}
        <span
          className={`font-mono text-xs font-medium rounded px-1.5 py-0.5 ${
            failed
              ? 'bg-red-500/10 text-red-700 dark:text-red-400'
              : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
          }`}
        >
          {shortType}
        </span>
        {!hideTargets && (
          <DeliveryTargets
            routed={row.routed}
            targetSpaceIds={row.targetSpaceIds}
            matchedRuleIds={row.matchedRuleIds}
            dynamicResolution={row.dynamicResolution}
            resolveSpaceName={resolveSpaceName}
            ruleHref={ruleHref}
          />
        )}
        <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
          {new Date(row.createdAt).toLocaleString()}
        </span>
      </div>
      {expanded && (
        <DeliveryDetails
          row={row}
          integrationId={rowIntegrationId}
          useEventDetail={useEventDetail}
          useRedeliver={useRedeliver}
        />
      )}
    </div>
  );
}

function formatDuration(ms?: number): string {
  if (ms == null) return 'Completed';
  const seconds = ms / 1000;
  return `Completed in ${seconds.toFixed(2)} seconds`;
}

// Stable no-op for callers that don't want a Redeliver button. We always
// call a hook here so React sees a constant hook call sequence — the prop
// is stable for the component's lifetime but ESLint's react-hooks/rules
// rule still flags conditional calls.
const useNoopRedeliver: UseRedeliverHook = () => ({
  mutateAsync: async () => {
    throw new Error('redeliver not available');
  },
  isPending: false,
});

function DeliveryDetails({
  row,
  integrationId,
  useEventDetail,
  useRedeliver,
}: {
  row: DeliveryRow;
  integrationId: string;
  useEventDetail: UseEventDetailHook;
  useRedeliver?: UseRedeliverHook;
}) {
  const { data: detail, isLoading } = useEventDetail(integrationId, row.eventId);
  const redeliverHook = useRedeliver ?? useNoopRedeliver;
  const redeliverMutation = redeliverHook(integrationId);
  const toast = useToast();

  const delivery = detail?.delivery;
  const request = delivery?.request;
  const response = delivery?.response;
  const responseStatus = response?.statusCode ?? detail?.responseStatusCode;
  const durationText = formatDuration(response?.durationMs ?? row.processingDurationMs);

  const handleRedeliver = async () => {
    try {
      const result = await redeliverMutation.mutateAsync(row.eventId);
      toast.success(result.message ?? 'Event queued for redelivery');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to redeliver event');
    }
  };

  return (
    <div className="px-4 pb-4 pt-1 bg-muted/30">
      <dl className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <div className="flex items-center gap-1.5">
          <dt className="text-muted-foreground">Event ID</dt>
          <dd className="font-mono text-foreground">{row.eventId}</dd>
          <CopyButton value={row.eventId} />
        </div>
        {row.deliveryId && row.deliveryId !== row.eventId && (
          <div className="flex items-center gap-1.5">
            <dt className="text-muted-foreground">Provider delivery ID</dt>
            <dd className="font-mono text-foreground">{row.deliveryId}</dd>
            <CopyButton value={row.deliveryId} />
          </div>
        )}
      </dl>
      {row.dynamicResolution && (
        <DynamicResolutionSection resolution={row.dynamicResolution} />
      )}
      <Tabs defaultValue="request">
        <div className="flex items-center gap-3 border-b mb-4">
          <TabsList className="h-auto rounded-none bg-transparent p-0">
            <TabsTrigger
              value="request"
              className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-3 pb-2 pt-1 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              Request
            </TabsTrigger>
            <TabsTrigger
              value="response"
              className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-3 pb-2 pt-1 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              <span>Response</span>
              {responseStatus != null && (
                <span
                  className={`ml-2 rounded px-1.5 py-0.5 text-xs font-mono ${
                    responseStatus >= 200 && responseStatus < 300
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}
                >
                  {responseStatus}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <div className="ml-auto flex items-center gap-3 pb-1">
            {useRedeliver && (
              <Button
                variant="secondary"
                size="sm"
                disabled={redeliverMutation.isPending}
                isLoading={redeliverMutation.isPending}
                onClick={handleRedeliver}
                title="Resend this webhook"
              >
                Redeliver
              </Button>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
              <Clock className="h-3 w-3" />
              {durationText}
            </span>
          </div>
        </div>

        <TabsContent value="request" className="mt-0">
          <DetailSections
            headers={request?.headers}
            bodyLabel="Payload"
            body={request?.body}
            contentType={request?.contentType}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="response" className="mt-0">
          <DetailSections
            headers={response?.headers}
            bodyLabel="Body"
            body={response?.body}
            contentType={detail?.contentType}
            isLoading={isLoading}
          />
          {row.errorMessage && (
            <div className="mt-4">
              <SectionHeading>Error</SectionHeading>
              <div className="rounded-md border bg-background p-3 text-sm text-red-600 dark:text-red-400 font-mono">
                {row.errorMessage}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SectionHeading({
  children,
  copyValue,
}: {
  children: React.ReactNode;
  copyValue?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <h4 className="text-sm font-semibold">{children}</h4>
      {copyValue && <CopyButton value={copyValue} />}
    </div>
  );
}

/**
 * Explains how a dynamically-routed event's target space was resolved at
 * delivery time (e.g. Slack DM sender → their personal space).
 */
function DynamicResolutionSection({
  resolution,
}: {
  resolution: DynamicResolution;
}) {
  const rows: Array<[string, string]> = [
    ['Resolver', resolution.resolver],
    ['Sender', resolution.senderId],
    ['Workspace', resolution.teamId],
    ['Resolved user', resolution.resolvedUserId],
  ];
  return (
    <div className="mb-4">
      <SectionHeading>Dynamic routing</SectionHeading>
      <div className="rounded-md border bg-background p-3">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-mono text-foreground break-all">{value}</dd>
            </div>
          ))}
          <dt className="text-muted-foreground">Cache</dt>
          <dd className="text-foreground">
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                resolution.cacheHit
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {resolution.cacheHit ? 'hit' : 'miss'}
            </span>
          </dd>
        </dl>
      </div>
    </div>
  );
}

function NotAvailable() {
  return (
    <div className="rounded-md border bg-background p-3 text-xs text-muted-foreground italic">
      Not available
    </div>
  );
}

function HeadersBlock({ headers }: { headers: Record<string, string> }) {
  const entries = Object.entries(headers);
  return (
    <div className="rounded-md border bg-background p-3 font-mono text-xs space-y-0.5 overflow-x-auto">
      {entries.map(([k, v]) => (
        <div key={k}>
          <span className="font-semibold">{k}:</span> {v}
        </div>
      ))}
    </div>
  );
}

function BodyBlock({ text }: { text: string }) {
  return (
    <pre className="rounded-md border bg-background p-3 font-mono text-xs overflow-x-auto max-h-96">
      {text}
    </pre>
  );
}

function BodyRenderer({ body, contentType }: { body: string; contentType: string | undefined }) {
  // JSON bodies get Prism syntax highlighting. Anything else (form-encoded,
  // plain text, XML, …) renders as a plain monospace pre so we don't
  // distort the payload. The copy button lives on the SectionHeading above,
  // so we hide JsonContent's built-in header to avoid a duplicate.
  if (isJsonContentType(contentType)) {
    return (
      <JsonContent maxHeight="24rem" showHeader={false}>
        {body}
      </JsonContent>
    );
  }
  return <BodyBlock text={body} />;
}

function DetailSections({
  headers,
  bodyLabel,
  body,
  contentType,
  isLoading,
}: {
  headers?: Record<string, string>;
  bodyLabel: string;
  body: string | undefined;
  contentType: string | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  return (
    <div className="space-y-4">
      <div>
        <SectionHeading copyValue={headers ? serializeHeaders(headers) : undefined}>
          Headers
        </SectionHeading>
        {headers && Object.keys(headers).length > 0 ? (
          <HeadersBlock headers={headers} />
        ) : (
          <NotAvailable />
        )}
      </div>
      <div>
        <SectionHeading copyValue={body}>{bodyLabel}</SectionHeading>
        {body ? <BodyRenderer body={body} contentType={contentType} /> : <NotAvailable />}
      </div>
    </div>
  );
}

export default function IntegrationDeliveriesCard({
  integrationId,
  useEvents,
  useEventDetail,
  useRedeliver,
  queryKeyPrefix,
  ruleHref,
  title = 'Recent Deliveries',
  description = 'Webhook events received from this integration. Live updates via WebSocket.',
  hideConnectionIndicator = false,
}: Props) {
  const { data: historical, isLoading } = useEvents(integrationId);
  const live = useIntegrationLiveEvents(integrationId, queryKeyPrefix);
  const { isConnected } = usePlatformEvents();
  const { data: spaces } = useMeSpaces();

  const spaceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of spaces?.items ?? []) map.set(s.spaceId, s.name);
    return map;
  }, [spaces]);
  const resolveSpaceName = (id: string) => spaceNameById.get(id);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const rows = useMemo(() => mergeRows(historical, live), [historical, live]);

  return (
    <Card>
      <CardHeader
        title={title}
        description={description}
        action={
          hideConnectionIndicator ? undefined : (
            <ConnectionIndicator isConnected={isConnected} />
          )
        }
      />
      {isLoading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No events received yet.</p>
      ) : (
        <div className="rounded-md border divide-y-0">
          {rows.map((row) => (
            <DeliveryRowView
              key={row.key}
              row={row}
              integrationId={integrationId}
              expanded={expandedKey === row.key}
              onToggle={() =>
                setExpandedKey((prev) => (prev === row.key ? null : row.key))
              }
              useEventDetail={useEventDetail}
              useRedeliver={useRedeliver}
              resolveSpaceName={resolveSpaceName}
              ruleHref={ruleHref}
            />
          ))}
        </div>
      )}
    </Card>
  );
}
